import { buildAuthHeaders } from "../forum/authHeaders";

const IS_LOCAL_FRONTEND =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);
const API_BASE_URL = IS_LOCAL_FRONTEND
  ? process.env.REACT_APP_API_BASE_URL_LOCAL || ""
  : process.env.REACT_APP_API_BASE_URL || "";

function apiUrl(path) {
  return API_BASE_URL ? `${API_BASE_URL}${path}` : path;
}

export const SUBSCRIPTION_ENDPOINTS = Object.freeze({
  plans: "/api/subscriptions/plans/",
  premium: "/api/subscriptions/premium/",
  premiumCheckout: "/api/subscriptions/premium/checkout/",
  premiumConfirmation: "/api/subscriptions/premium/confirmation/",
  premiumBenefits: "/api/subscriptions/premium/benefits/",
  premiumBoosts: "/api/subscriptions/premium/boosts/",
  portal: "/api/subscriptions/portal/",
  adminFinance: "/api/subscriptions/admin/finance/",
  me: "/api/subscriptions/me/",
  change: "/api/subscriptions/change/",
  cancel: "/api/subscriptions/cancel/",
  reactivate: "/api/subscriptions/reactivate/",
  professionalModule: "/api/subscriptions/professional-module/",
  consumeUsage: "/api/subscriptions/usage/consume/",
});

export function createIdempotencyKey(prefix = "subscription") {
  const id = (typeof window !== "undefined" && window.crypto?.randomUUID?.()) || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}:${id}`.slice(0, 160);
}

async function readResponse(response) {
  if (response.status === 204 && response.ok) return {};
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(payload?.detail || "subscription.request_failed");
    error.status = response.status;
    error.code = payload?.code || "request_failed";
    error.payload = payload;
    throw error;
  }
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    const error = new Error("subscription.invalid_response");
    error.code = "invalid_response";
    throw error;
  }
  return payload;
}

async function subscriptionRequest(path, { method = "GET", body, authenticated = true, idempotencyKey, signal } = {}) {
  const headers = authenticated ? buildAuthHeaders({ Accept: "application/json" }) : { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (!["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase()) && typeof document !== "undefined") {
    const csrf = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
    if (csrf) headers["X-CSRFToken"] = decodeURIComponent(csrf[1]);
  }
  if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;
  try {
    const response = await fetch(apiUrl(path), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
    return await readResponse(response);
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    if (!error?.status) {
      error.code = typeof navigator !== "undefined" && navigator.onLine === false
        ? "offline"
        : error.code || "network_error";
    }
    throw error;
  }
}

export function fetchSubscriptionPlans(options = {}) {
  return subscriptionRequest(SUBSCRIPTION_ENDPOINTS.plans, {
    authenticated: false,
    signal: options.signal,
  });
}

export function fetchPremiumOffer(options = {}) {
  return subscriptionRequest(SUBSCRIPTION_ENDPOINTS.premium, {
    authenticated: false,
    signal: options.signal,
  });
}

export function createPremiumCheckout(
  legalAcceptance,
  idempotencyKey = createIdempotencyKey("premium-checkout")
) {
  return subscriptionRequest(SUBSCRIPTION_ENDPOINTS.premiumCheckout, {
    method: "POST",
    body: {
      plan_key: "premium",
      confirmed: true,
      terms_version: legalAcceptance?.termsVersion,
      terms_sha256: legalAcceptance?.termsSha256,
      immediate_performance_requested:
        legalAcceptance?.immediatePerformanceRequested === true,
    },
    idempotencyKey,
  });
}

export function fetchPremiumConfirmation(sessionId, options = {}) {
  const query = new URLSearchParams({ session_id: String(sessionId || "") });
  return subscriptionRequest(`${SUBSCRIPTION_ENDPOINTS.premiumConfirmation}?${query}`, {
    signal: options.signal,
  });
}

export function fetchPremiumBenefits(options = {}) {
  return subscriptionRequest(SUBSCRIPTION_ENDPOINTS.premiumBenefits, {
    signal: options.signal,
  });
}

export function activatePremiumBoost(
  listingId,
  idempotencyKey = createIdempotencyKey("premium-boost")
) {
  return subscriptionRequest(SUBSCRIPTION_ENDPOINTS.premiumBoosts, {
    method: "POST",
    body: { listing_id: String(listingId || "").trim() },
    idempotencyKey,
  });
}

export function openSubscriptionPortal(
  idempotencyKey = createIdempotencyKey("subscription-portal")
) {
  return subscriptionRequest(SUBSCRIPTION_ENDPOINTS.portal, {
    method: "POST",
    body: {},
    idempotencyKey,
  });
}

export function fetchSubscriptionFinance(options = {}) {
  return subscriptionRequest(SUBSCRIPTION_ENDPOINTS.adminFinance, {
    signal: options.signal,
  });
}

export function fetchMySubscription(options = {}) {
  return subscriptionRequest(SUBSCRIPTION_ENDPOINTS.me, { signal: options.signal });
}

export function requestPlanChange(planCode, billingCycle, idempotencyKey = createIdempotencyKey("plan-change")) {
  return subscriptionRequest(SUBSCRIPTION_ENDPOINTS.change, {
    method: "POST",
    body: { plan_code: planCode, billing_cycle: billingCycle },
    idempotencyKey,
  });
}

export function cancelMySubscription(idempotencyKey = createIdempotencyKey("cancel")) {
  return subscriptionRequest(SUBSCRIPTION_ENDPOINTS.cancel, {
    method: "POST",
    body: {},
    idempotencyKey,
  });
}

export function reactivateMySubscription(idempotencyKey = createIdempotencyKey("reactivate")) {
  return subscriptionRequest(SUBSCRIPTION_ENDPOINTS.reactivate, {
    method: "POST",
    body: {},
    idempotencyKey,
  });
}

export function updateProfessionalModule(module, idempotencyKey = createIdempotencyKey("module")) {
  return subscriptionRequest(SUBSCRIPTION_ENDPOINTS.professionalModule, {
    method: "PATCH",
    body: { module },
    idempotencyKey,
  });
}

export function fetchPremiumSpace(featureKey, options = {}) {
  return subscriptionRequest(`/api/subscriptions/premium/spaces/${encodeURIComponent(featureKey)}/`, {
    authenticated: false, signal: options.signal,
  });
}

export function fetchPremiumWorkspace(featureKey, options = {}) {
  return subscriptionRequest(`/api/subscriptions/premium/spaces/${encodeURIComponent(featureKey)}/workspace/?page=${options.page || 1}`, {
    signal: options.signal,
  });
}

export const fetchPremiumDashboard = (options = {}) => subscriptionRequest("/api/subscriptions/premium/dashboard/", options);
export const savePremiumDashboardSettings = (body) => subscriptionRequest("/api/subscriptions/premium/dashboard/settings/", { method: "PUT", body });
export const fetchPremiumTools = (key, options = {}) => subscriptionRequest(`/api/subscriptions/premium/spaces/${encodeURIComponent(key)}/tools/`, options);
export const savePremiumTool = (key, body, id) => subscriptionRequest(id ? `/api/subscriptions/premium/tools/${encodeURIComponent(id)}/` : `/api/subscriptions/premium/spaces/${encodeURIComponent(key)}/tools/`, { method: id ? "PUT" : "POST", body });
export const archivePremiumTool = (id, version) => subscriptionRequest(`/api/subscriptions/premium/tools/${encodeURIComponent(id)}/archive/`, { method: "POST", body: { version } });

export const studioRequest = (path = "", options = {}) => subscriptionRequest(`/api/subscriptions/premium/studio/${path}`, options);
export const memberExtrasRequest = (path = "", options = {}) => subscriptionRequest(`/api/subscriptions/premium/${path}`, options);
